import React, { ReactNode, useCallback, useState, useMemo, FC } from "react"
import {
  AspectRatio,
  Box,
  Flex,
  Image,
  SimpleGrid,
  Text,
  TextProps,
  BoxProps,
  FlexProps,
  createStylesContext,
  useMultiStyleConfig,
} from "@chakra-ui/react"
import { SelectLayoutEnum } from "../../../api/QuestionType"
import { ItemSizeEnum } from "./ItemSizeEnum"

export { SelectLayoutEnum }

const transitionDuration = "250ms"

type ImageVariant = "DEFAULT" | "TRANSPARENT"
const _getProps = (v: ImageVariant) =>
  v === "DEFAULT"
    ? {
        ratio: 7 / 3,
      }
    : {
        ratio: 7 / 6,
        bgImage: "var(--bg-transparent-image)",
        bgSize: "100% 200%",
        bgPosition: "center calc(-100% - 89px)",
        bgRepeat: "no-repeat",
        sx: {
          "img.chakra-image": {
            "object-fit": "contain",
          },
        },
      }
const STYLE_CONTEXT_NAME = "SingleSelectItem"
const [StylesProvider, useStyles] = createStylesContext(STYLE_CONTEXT_NAME)

export const VariantImage: FC<{ src: string; variant?: ImageVariant; zIndex?: number }> = ({
  src,
  variant = "DEFAULT",
  zIndex,
}) => (
  <AspectRatio w="100%" zIndex={zIndex} {..._getProps(variant)}>
    <Image src={src} align="center bottom" />
  </AspectRatio>
)

const _isImageLayout = (layout: SelectLayoutEnum) =>
  [
    SelectLayoutEnum.ONE_COLUMN_IMAGES,
    SelectLayoutEnum.TWO_COLUMNS_IMAGES,
    SelectLayoutEnum.TWO_COLUMNS_IMAGES_TRANSPARENT,
  ].includes(layout)

const Title: FC<{ children: ReactNode; textStyle: TextProps["textStyle"] }> = ({
  children,
  textStyle = "Subtitle/Primary",
}) => (
  <Text as="div" textStyle={textStyle}>
    {children}
  </Text>
)

const Description: FC<{ children: ReactNode; color?: string }> = ({ children }) => (
  <Text as="div" textStyle="Paragraph/Secondary" opacity={0.7}>
    {children}
  </Text>
)
/* TODO remove isSmall */
export const VariantTitle: FC<
  { title: string; description?: string; isSmall?: boolean } & FlexProps
> = ({ title, description, isSmall = false, ...props }) => (
  <Flex direction="column" w="full" gap={1} {...props}>
    <Title textStyle={isSmall ? "Subtitle/Secondary" : "Subtitle/Primary"}>{title}</Title>
    {description && <Description>{description}</Description>}
  </Flex>
)

const VariantContent: FC<{
  icon?: string
  title: string
  description?: string
  /* selected?: boolean */
  isSmall?: boolean
}> = ({ icon, title, description, isSmall = false }) => {
  const styles = useStyles()
  return (
    <Box display="flex" gap={4} overflow="hidden" w="full" __css={styles["content"]}>
      {icon && (
        <Image
          src={icon}
          w={12}
          h={12}
          borderRadius="md"
          align="center center"
          objectFit="scale-down"
          transitionDuration={transitionDuration}
          transitionProperty="filter"
        />
      )}
      <VariantTitle title={title} description={description} isSmall={isSmall} />
    </Box>
  )
}
/* TODO rename to SingleSelectItemProps */
type SingleSelectVariantProps = BoxProps & {
  selected: boolean
  onClick: () => void
  size?: ItemSizeEnum
  children: ReactNode
}

/* TODO rename to SingleSelectItem */
/* TODO use Radio for SingleSelect and remove selected from variant   */
export const SingleSelectVariant: FC<SingleSelectVariantProps> = ({
  children,
  onClick,
  selected: isSelected = false,
  size = ItemSizeEnum.ONE_COLUMN,
  ...flexProps
}) => {
  const styles = useMultiStyleConfig(STYLE_CONTEXT_NAME, {
    variant: isSelected ? "selected" : "default",
    size,
  })
  return (
    <StylesProvider value={styles}>
      <Box
        __css={styles["item"]}
        display="flex"
        flexDirection="column"
        w="100%"
        userSelect="none"
        transitionDuration={transitionDuration}
        transitionProperty="background-color, color, transform"
        cursor="pointer"
        overflow="hidden"
        onClick={onClick}
        textStyle="Subtitle/Primary"
        {...flexProps}
      >
        {children}
      </Box>
    </StylesProvider>
  )
}

export type Variant = {
  title: string
  description?: string
  icon?: string
  image?: string
}

export const SingleSelect: FC<{
  variants: Variant[]
  onChange: (variant: string) => void
  layout: SelectLayoutEnum
}> = ({ variants, onChange, layout }) => {
  const [selected, setSelected] = useState<string | undefined>(undefined)
  const onSelect = useCallback(
    (variant: string) => {
      setSelected(variant)
      onChange(variant)
    },
    [setSelected, onChange]
  )
  const columns: number = useMemo(
    () =>
      layout === SelectLayoutEnum.ONE_COLUMN || layout === SelectLayoutEnum.ONE_COLUMN_IMAGES
        ? 1
        : 2,
    [layout]
  )
  const isSmall: boolean = useMemo(() => columns === 2, [columns])
  const isImagesLayout: boolean = useMemo(() => _isImageLayout(layout), [layout])
  const itemLayout = useMemo(
    () =>
      (withIcon: boolean): ItemSizeEnum => {
        if (layout === SelectLayoutEnum.ONE_COLUMN) {
          return withIcon ? ItemSizeEnum.ONE_COLUMN_ICON : ItemSizeEnum.ONE_COLUMN
        }
        if (layout === SelectLayoutEnum.TWO_COLUMNS) {
          return withIcon ? ItemSizeEnum.TWO_COLUMNS_ICONS : ItemSizeEnum.TWO_COLUMNS
        }
        if (layout === SelectLayoutEnum.ONE_COLUMN_IMAGES) {
          return ItemSizeEnum.ONE_COLUMN_IMAGE
        }
        if (layout === SelectLayoutEnum.TWO_COLUMNS_IMAGES) {
          return ItemSizeEnum.TWO_COLUMNS_IMAGES
        }
        if (layout === SelectLayoutEnum.TWO_COLUMNS_IMAGES_TRANSPARENT) {
          return ItemSizeEnum.TWO_COLUMNS_IMAGES_TRANSPARENT
        }
        return ItemSizeEnum.ONE_COLUMN
      },
    [layout]
  )

  return (
    <SimpleGrid columns={columns} spacing={[2, 2]}>
      {variants.map(({ title, description, image, icon }, i) => {
        const isSelected = selected === title
        if (isImagesLayout && image) {
          return (
            <SingleSelectVariant
              minH="72px"
              key={i + title}
              selected={isSelected}
              onClick={() => onSelect(title)}
              size={itemLayout(false)}
            >
              <VariantImage
                src={image}
                variant={
                  layout === SelectLayoutEnum.TWO_COLUMNS_IMAGES_TRANSPARENT
                    ? "TRANSPARENT"
                    : "DEFAULT"
                }
              />

              <VariantContent isSmall={isSmall} title={title} description={description} />
            </SingleSelectVariant>
          )
        }

        return (
          <SingleSelectVariant
            key={i + title}
            selected={isSelected}
            onClick={() => onSelect(title)}
            size={itemLayout(Boolean(icon))}
            minH="72px"
          >
            <VariantContent icon={icon} isSmall={isSmall} title={title} description={description} />
          </SingleSelectVariant>
        )
      })}
    </SimpleGrid>
  )
}
